/*
 * Copyright (c) 2020-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import type { BreadcrumbItem } from "@/components/ui/breadcrumbs";
import { submissionKeys, useSubmission } from "@/api/hooks/useSubmissions";
import { incidentKeys, useIncident } from "@/api/hooks/useIncidents";
import type { SubmissionDetail, Incident } from "@/lib/types";

/** Detail cache may be camelCase SubmissionDetail or raw API snake_case. */
function submissionBreadcrumbLabel(detail: unknown, fallbackId: string): string {
  if (!detail || typeof detail !== "object") {
    return fallbackId;
  }
  const o = detail as Record<string, unknown>;
  const submissionName =
    typeof o.submission_name === "string" && o.submission_name.trim() !== ""
      ? o.submission_name.trim()
      : null;
  if (submissionName) {
    return submissionName;
  }
  const fromSnake =
    typeof o.ticket_number === "string" && o.ticket_number.trim() !== ""
      ? o.ticket_number.trim()
      : null;
  const fromCamel =
    typeof o.ticketNumber === "string" && o.ticketNumber.trim() !== ""
      ? o.ticketNumber.trim()
      : null;
  const tn = fromSnake ?? fromCamel;
  if (tn) {
    return tn;
  }
  return fallbackId;
}

// Route name mappings
const routeNames: Record<string, string> = {
  "/": "Dashboard",
  "/submissions": "Submissions",
  "/submissions/new": "New Submission",
  "/incidents": "Incidents",
  "/incidents/analytics": "Analytics",
  "/analytics": "Analytics",
  "/activity": "Activity",
  "/profile": "Profile",
  "/admin": "Administration",
  "/admin/measures": "Measures",
  "/admin/thresholds": "Thresholds",
  "/admin/identity-providers": "Identity Providers",
  "/admin/security": "Security",
  "/admin/audit": "Audit Log",
  "/admin/state-configuration": "State Configuration",
  "/admin/system-settings": "System Settings",
  "/admin/integrations": "Integrations",
  "/admin/change-requests": "Change Requests",
  "/admin/users": "Users",
  "/admin/validation-config": "Validation Rules",
  "/help": "Help & Documentation",
};

// Dynamic route patterns and their name generators
interface DynamicRoute {
  pattern: RegExp;
  parentPath: string;
  getName: (
    params: string[],
    queryClient?: ReturnType<typeof useQueryClient>,
    submissionData?: SubmissionDetail,
    incidentData?: Incident
  ) => string;
}

const STATIC_INCIDENT_SUBPATHS = ["analytics"];

const dynamicRoutes: DynamicRoute[] = [
  {
    pattern: /^\/submissions\/([^/]+)$/,
    parentPath: "/submissions",
    getName: (params, queryClient, submissionData) => {
      const submissionId = params[0];
      /* v8 ignore next -- unreachable: "/submissions/new" is a static routeNames entry (line 49), so the breadcrumb loop resolves it before ever reaching this dynamic route's getName; kept as a defensive fallback if that static entry is ever removed */
      if (submissionId === "new") {
        /* v8 ignore next -- see the `if` above; this consequent can never run given the static routeNames precedence */
        return "New Submission";
        /* v8 ignore next -- closing brace of the unreachable consequent block above */
      }

      const detail =
        submissionData ??
        queryClient?.getQueryData<SubmissionDetail>(submissionKeys.detail(submissionId));
      return submissionBreadcrumbLabel(detail, submissionId);
    },
  },
  {
    pattern: /^\/incidents\/([^/]+)$/,
    parentPath: "/incidents",
    getName: (params, queryClient, _submissionData, incidentData) => {
      const incidentId = params[0];

      /* v8 ignore next -- unreachable: "/incidents/analytics" is a static routeNames entry (line 51), so the breadcrumb loop resolves it before ever reaching this dynamic route's getName; kept as a defensive fallback if that static entry is ever removed */
      if (STATIC_INCIDENT_SUBPATHS.includes(incidentId)) {
        /* v8 ignore next -- see the `if` above; this consequent can never run given the static routeNames precedence */
        return incidentId.charAt(0).toUpperCase() + incidentId.slice(1);
        /* v8 ignore next -- closing brace of the unreachable consequent block above */
      }

      const data =
        incidentData ||
        (queryClient && queryClient.getQueryData<Incident>(incidentKeys.detail(incidentId)));
      if (data?.incident_number) {
        return data.incident_number;
      }
      return incidentId;
    },
  },
];

export function useBreadcrumbs(): BreadcrumbItem[] {
  const locationObj = useLocation();
  const location = locationObj.pathname;
  const queryClient = useQueryClient();
  const submissionMatch = location.match(/^\/submissions\/([^/]+)$/);
  const submissionId =
    submissionMatch && submissionMatch[1] !== "new" ? submissionMatch[1] : undefined;
  const { data: submissionData } = useSubmission(submissionId);

  const incidentMatch = location.match(/^\/incidents\/([^/]+)$/);
  const incidentId =
    incidentMatch && !STATIC_INCIDENT_SUBPATHS.includes(incidentMatch[1])
      ? incidentMatch[1]
      : undefined;
  const { data: incidentData } = useIncident(incidentId);

  const breadcrumbs = useMemo(() => {
    // Don't show breadcrumbs for root
    if (location === "/") {
      return [];
    }

    const items: BreadcrumbItem[] = [];

    // Always start with Dashboard/Home
    items.push({
      label: "Dashboard",
      href: "/",
    });

    // Split path into segments
    const segments = location.split("/").filter(Boolean);
    let currentPath = "";

    for (let i = 0; i < segments.length; i++) {
      currentPath += "/" + segments[i];

      // Check for static route name
      if (routeNames[currentPath]) {
        items.push({
          label: routeNames[currentPath],
          href: currentPath,
        });
      } else {
        // Check for dynamic routes
        let matched = false;
        for (const dynamicRoute of dynamicRoutes) {
          const match = currentPath.match(dynamicRoute.pattern);
          if (match) {
            const params = match.slice(1);
            const cachedData =
              submissionData ||
              queryClient.getQueryData<SubmissionDetail>(submissionKeys.detail(params[0]));
            items.push({
              label: dynamicRoute.getName(params, queryClient, cachedData, incidentData),
              href: currentPath,
            });
            matched = true;
            break;
          }
        }

        // If no match, use a formatted version of the segment
        if (!matched) {
          const formattedLabel = segments[i]
            .split("-")
            .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ");
          items.push({
            label: formattedLabel,
            href: currentPath,
          });
        }
      }
    }

    return items;
  }, [location, queryClient, submissionData, incidentData]);

  return breadcrumbs;
}

// Helper to check if breadcrumbs should be shown
export function useShouldShowBreadcrumbs(): boolean {
  const locationObj = useLocation();
  const location = locationObj.pathname;
  // Show breadcrumbs for any page deeper than root
  return location !== "/";
}

export default useBreadcrumbs;
